import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { queryContract } from '../Components/functions/Contract';
import { Testnet } from '@nibiruchain/nibijs';

const initialState = {
  liquidationPrice: 0,
  currentLeverage: 1,
  currentCollateral: 0,
  currentPrice: 0,
  dubaiPrice: 0,
  isLoading: false,
  error: null,
  tradeData: null,
  feeStructure: null
};

// Fetch actual trade data with correct liquidation price
export const fetchTradeDataWithLiquidation = createAsyncThunk(
  'liquidation/fetchTradeData',
  async ({ trader, tradeIndex }, { rejectWithValue }) => {
    try {
      const message = {
        get_trade_data: {
          trader: trader,
          index: tradeIndex
        }
      };

      const result = await queryContract(
        process.env.REACT_APP_PERP_SMART_CONTRACT,
        message,
        Testnet(2).endptTm
      );

      if (!result || !result.data) {
        throw new Error('No trade data returned');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to fetch trade data:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Fetch fee structure from contract
export const fetchFeeStructure = createAsyncThunk(
  'liquidation/fetchFeeStructure',
  async ({ feeIndex = "FeeIndex(0)" }, { rejectWithValue }) => {
    try {
      const message = {
        get_fees: {
          index: feeIndex
        }
      };

      const result = await queryContract(
        process.env.REACT_APP_PERP_SMART_CONTRACT,
        message,
        Testnet(2).endptTm
      );

      return result;
    } catch (error) {
      console.error('Failed to fetch fee structure:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Calculate liquidation price with contract fees
export const calculateEnhancedLiquidation = createAsyncThunk(
  'liquidation/calculateEnhanced',
  async ({ price, leverage, collateral, isLong, marketIndex, collateralIndex }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      let feeStructure = state.liquidation.feeStructure;

      // Fetch fee structure if not already cached
      if (!feeStructure) {
        const feeResult = await dispatch(fetchFeeStructure({ feeIndex: "FeeIndex(0)" }));
        if (feeResult.type.endsWith('/fulfilled')) {
          feeStructure = feeResult.payload;
        }
      }



      // Perform calculation
      const numericPrice = Number(price);
      const numericLeverage = Number(leverage);
      const numericCollateral = Number(collateral);

      // Use contract fees or fallback to defaults
      const openFee = feeStructure?.open_fee_p ? Number(feeStructure.open_fee_p) : 0.001;
      const closeFee = feeStructure?.close_fee_p ? Number(feeStructure.close_fee_p) : 0.001;

      // Liquidation calculation
      const LIQ_THRESHOLD_P = 0.9;
      const totalFees = openFee + closeFee;
      
      // Calculate effective collateral after fees
      const effectiveCollateral = numericCollateral * (1 - numericLeverage * totalFees);
      
      // Calculate liquidation threshold
      const liquidationThreshold = LIQ_THRESHOLD_P * effectiveCollateral;
      
      // Calculate price distance to liquidation
      const priceDistance = (liquidationThreshold * numericPrice) / (effectiveCollateral * numericLeverage);
      
      // Calculate final liquidation price based on position type
      const liquidationPrice = isLong 
        ? numericPrice - priceDistance  // Long: price goes down
        : numericPrice + priceDistance; // Short: price goes up

      // Log calculation details for debugging
      console.log('Liquidation calculation:', {
        position: isLong ? 'LONG' : 'SHORT',
        openPrice: numericPrice,
        leverage: numericLeverage,
        collateral: numericCollateral,
        openFee,
        closeFee,
        totalFees,
        effectiveCollateral,
        liquidationThreshold,
        priceDistance,
        liquidationPrice,
        percentageFromEntry: ((liquidationPrice - numericPrice) / numericPrice * 100).toFixed(2) + '%'
      });

      return {
        liquidationPrice,
        openPrice: numericPrice,
        leverage: numericLeverage,
        collateral: numericCollateral,
        feeStructure,
        calculationDetails: {
          openFee,
          closeFee,
          borrowingFeeAmount: 0, 
          effectiveCollateral,
          liquidationThreshold,
          priceDistance
        }
      };
    } catch (error) {
      console.error('Failed to calculate enhanced liquidation:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Simple liquidation calculation (fallback mechanism)
const calculateSimpleLiquidation = ({ price, leverage, collateral, isLong }) => {
  const LIQ_THRESHOLD_P = 0.9;
  const OPEN_FEE = 0.001;
  const CLOSE_FEE = 0.001;

  const numericPrice = Number(price);
  const numericLeverage = Number(leverage);
  const numericCollateral = Number(collateral);

  const collateralNetOfFees = numericCollateral * (1 - numericLeverage * (OPEN_FEE + CLOSE_FEE));
  const collateralLiqNegativePnl = LIQ_THRESHOLD_P * collateralNetOfFees;
  const liqPriceDistance = (collateralLiqNegativePnl * numericPrice) / 
                          (collateralNetOfFees * numericLeverage);

  const liquidationPrice = isLong 
    ? numericPrice - liqPriceDistance
    : numericPrice + liqPriceDistance;

  return {
    liquidationPrice,
    openPrice: numericPrice,
    leverage: numericLeverage,
    collateral: numericCollateral
  };
};

export const liquidationSlice = createSlice({
  name: 'liquidation',
  initialState,
  reducers: {
    // Liquidation calculation (for immediate UI updates)
    updateLiquidationPrice: (state, action) => {
      const result = calculateSimpleLiquidation(action.payload);
      
      state.liquidationPrice = result.liquidationPrice;
      state.currentPrice = result.openPrice;
      state.currentLeverage = result.leverage;
      state.currentCollateral = result.collateral;
    },
    
    // Set Dubai price
    setDubaiPrice: (state, action) => {
      state.dubaiPrice = action.payload;
      state.currentPrice = action.payload;
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Reset liquidation state
    resetLiquidation: (state) => {
      state.liquidationPrice = 0;
      state.currentLeverage = 1;
      state.currentCollateral = 0;
      state.currentPrice = 0;
      state.error = null;
      state.tradeData = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchTradeDataWithLiquidation
      .addCase(fetchTradeDataWithLiquidation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTradeDataWithLiquidation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tradeData = action.payload;
        
        // Use the EXACT liquidation price from contract
        if (action.payload.liquidation_price) {
          state.liquidationPrice = Number(action.payload.liquidation_price);
        }
        
        // Update other relevant state
        if (action.payload.trade) {
          state.currentLeverage = Number(action.payload.trade.leverage);
          state.currentCollateral = Number(action.payload.trade.collateral_amount) / 1000000;
          state.currentPrice = Number(action.payload.trade.open_price);
        }
      })
      .addCase(fetchTradeDataWithLiquidation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Handle fetchFeeStructure
      .addCase(fetchFeeStructure.fulfilled, (state, action) => {
        state.feeStructure = action.payload;
      })
      .addCase(fetchFeeStructure.rejected, (state, action) => {
        console.error('Failed to fetch fee structure:', action.payload);
      })
      
      // Handle calculateEnhancedLiquidation
      .addCase(calculateEnhancedLiquidation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(calculateEnhancedLiquidation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.liquidationPrice = action.payload.liquidationPrice;
        state.currentPrice = action.payload.openPrice;
        state.currentLeverage = action.payload.leverage;
        state.currentCollateral = action.payload.collateral;
        
        // Cache fee structure for future use
        if (action.payload.feeStructure) {
          state.feeStructure = action.payload.feeStructure;
        }
      })
      .addCase(calculateEnhancedLiquidation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        
        // Fallback to simple calculation on error
        console.warn('Enhanced calculation failed, using simple fallback');
      });
  }
});

export const { 
  updateLiquidationPrice, 
  setDubaiPrice, 
  clearError, 
  resetLiquidation 
} = liquidationSlice.actions;

export default liquidationSlice.reducer;